import {Component, inject, computed, model, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatInputModule} from "@angular/material/input"
import {MatButtonModule} from "@angular/material/button"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {MutationCreateOrganizationInput} from "@generated"
import {CreateOrganizationGQL} from "@app/platform/components/organizations/add-organization-dialog/add-organization-dialog.generated"

type AddOrganizationData = Partial<MutationCreateOrganizationInput>

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-organization-dialog",
    imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule],
    templateUrl: "./add-organization-dialog.component.html",
    styleUrl: "./add-organization-dialog.component.scss",
})
export class AddOrganizationDialogComponent {
    readonly notifications = inject(NotificationsService)
    readonly createOrganizationGql = inject(CreateOrganizationGQL)
    readonly dialogRef = inject(MatDialogRef<AddOrganizationDialogComponent>)

    readonly $name = model<string>("", {alias: "name"})
    readonly $url = model<string>("", {alias: "url"})

    readonly $data = computed<AddOrganizationData>(() => {
        return {
            name: this.$name(),
            url: this.$url(),
        }
    })

    readonly $canConfirmCreate = computed(() => !!this.$data().name && !!this.$data().url)

    async confirmCreate() {
        const data = this.$data()
        await this.notifications.withUserFeedback(
            async () => {
                const {createOrganization: organization} = await mutateThrowingErrors(this.createOrganizationGql)({
                    input: data as MutationCreateOrganizationInput,
                })
                // Close dialog with the created organization
                this.dialogRef.close(organization)
                return organization
            },
            {
                success: "Organization created.",
                error: "Cannot create organization.",
            },
        )
    }
}
